import type { ReactNode } from 'react';
import { Button } from '@monksflow/monks-ui';
import { ArrowLeft } from 'lucide-react';

import { StepTracker, type StepState } from '@/shared/ui/StepTracker/StepTracker';
import { useTranslation } from 'react-i18next';

export interface WizardLayoutProps {
  /** Title shown in the configuration panel header. */
  configTitle: string;
  /** Step labels rendered in the step tracker. */
  steps: string[];
  /** Index of the active step (pass a value past the last index to mark all complete). */
  activeStep: number;
  /**
   * Optional per-step state overrides. When provided, each entry takes precedence
   * over `activeStep` for that step — use it to mark earlier steps complete while a
   * later step is inactive (e.g. a failed run: Setup complete, Run inactive), which
   * no single `activeStep` value can express.
   */
  stepStates?: ReadonlyArray<StepState>;
  /** Invoked when the back arrow in the configuration header is clicked. */
  onBack?: () => void;
  /** Content rendered in the configuration panel below the step tracker. */
  configContent: ReactNode;
  /** Right-hand pane content. Rendered inside the shared pane card. */
  children: ReactNode;
  /**
   * Whether the left configuration panel is visible. Defaults to true.
   * Pass a state value to support a collapsible panel (e.g. a report view).
   */
  showConfigPanel?: boolean;
}

/**
 * Two-pane layout shared by the Content Test and Focus Group flows: a fixed-width
 * configuration panel (back button, title, step tracker, scrollable content) beside
 * a flexible pane. Both panels render as rounded cards on the page background, so
 * consumers only supply the configuration content and the pane content.
 */
export const WizardLayout = ({
  configTitle,
  steps,
  activeStep,
  stepStates,
  onBack,
  configContent,
  children,
  showConfigPanel = true,
}: WizardLayoutProps) => {
  const { t } = useTranslation('common');
  return (
    <div className="flex h-full min-h-0 gap-4 p-4">
      {showConfigPanel && (
        <aside className="flex w-[408px] shrink-0 flex-col overflow-hidden rounded-lg bg-background">
          <div className="flex min-h-16 items-center gap-2 border-b border-border py-3 pl-3 pr-6">
            <Button
              variant="ghost"
              size="mini"
              className="h-8 w-8 p-0"
              aria-label={t('components.wizardLayout.goBack')}
              onClick={onBack}
            >
              <ArrowLeft size={20} />
            </Button>
            <span className="flex-1 text-center text-base font-medium text-foreground">
              {configTitle}
            </span>
          </div>
          <StepTracker
            activeStep={activeStep}
            steps={steps}
            stepStates={stepStates}
            variant="compact"
          />
          <div className="flex flex-1 flex-col items-center overflow-auto px-6 pt-12 mb-4">
            {configContent}
          </div>
        </aside>
      )}
      <section className="flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-background">
        {children}
      </section>
    </div>
  );
};

WizardLayout.displayName = 'WizardLayout';
